import { ScientificChartConfig } from '../../../../types';

/**
 * Default configuration for Scientific Charts
 * Based on Matplotlib/MATLAB conventions for scientific visualization
 */
export const DEFAULT_SCIENTIFIC_CONFIG: ScientificChartConfig = {
    plotType: 'scatter',
    subplots: {
        rows: 1,
        cols: 1
    },

    fieldMappings: {
        xField: undefined,
        yField: undefined,
        zField: undefined,
        colorField: undefined,
        sizeField: undefined,
        seriesField: undefined,
        yErrorField: undefined,
        xErrorField: undefined,
        yErrorUpperField: undefined,
        yErrorLowerField: undefined,
        yUpperBoundField: undefined,
        yLowerBoundField: undefined,
        uField: undefined,
        vField: undefined
    },

    axisOptions: {
        logScaleX: false,
        logScaleY: false,
        logScaleZ: false,
        grid: true,
        minorTicks: false,
        minorGrid: false,
        axisFormat: 'plain',
        gridColor: '#333333',
        gridThickness: 1
    },

    analysis: {
        trendline: 'none',
        showEquation: false,
        slopeMode: 'overall',
        slopeRange: { min: 0, max: 100 },
        confidenceInterval: false,
        confidenceOpacity: 0.2
    },

    errorBars: {
        show: false,
        mode: 'none',
        constantValue: 1,
        direction: 'symmetric',
        capSize: 5,
        lineThickness: 1,
        showConnectingLine: false
    },

    transformations: {
        x: 'none',
        y: 'none',
        normalize: false,
        standardize: false
    },

    style: {
        colormap: 'weaverGold',  // Changed from viridis to match WeaverBI theme
        showColorbar: true,
        markerShape: 'circle',
        markerSize: 6,
        markerSizeMin: 2,
        markerSizeMax: 20,
        markerOutline: false,
        lineStyle: 'solid',
        lineWidth: 2,
        curveSmoothing: false,
        alpha: 1,
        showPointMarkers: true
    },

    legend: {
        show: true,
        position: 'right'
    },

    animation: {
        fadeIn: true,
        duration: 300
    },

    heatmap: {
        colorScale: 'sequential',
        resolution: 20,
        showContours: false,
        contourCount: 10,
        interpolation: true
    },

    vector: {
        scale: 1,
        arrowSize: 10,
        normalize: false,
        autoGrid: false,
        magnitudeColoring: false
    },

    threeD: {
        elevation: 30,
        azimuth: 45,
        projection: 'perspective',
        colorGradient: 'viridis',
        pointSize: 4,
        depthShading: true,
        showAxisBox: true,
        wireframeMode: false,
        meshDensity: 20
    }
};

// Color palettes for scientific visualization
export const SCIENTIFIC_COLOR_PALETTES = {
    viridis: ['#440154', '#482878', '#3e4989', '#31688e', '#26828e', '#1f9e89', '#35b779', '#6ece58', '#b5de2b', '#fde725'],
    plasma: ['#0d0887', '#46039f', '#7201a8', '#9c179e', '#bd3786', '#d8576b', '#ed7953', '#fb9f3a', '#fdca26', '#f0f921'],
    turbo: ['#30123b', '#4662d7', '#36aaf9', '#1ae4b6', '#72fe5e', '#c8ef34', '#faba39', '#f66b19', '#ca2a04', '#7a0403'],
    inferno: ['#000004', '#1b0c41', '#4a0c6b', '#781c6d', '#a52c60', '#cf4446', '#ed6925', '#fb9b06', '#f7d13d', '#fcffa4'],
    magma: ['#000004', '#180f3d', '#440f76', '#721f81', '#9e2f7f', '#cd4071', '#f1605d', '#fd9668', '#feca8d', '#fcfdbf'],

    // Diverging palettes
    coolwarm: ['#3b4cc0', '#5977e3', '#7b9ff9', '#9ebeff', '#c0d4f5', '#f2cbb7', '#f7a889', '#ee7b56', '#d6503e', '#b40426'],
    spectral: ['#9e0142', '#d53e4f', '#f46d43', '#fdae61', '#fee08b', '#e6f598', '#abdda4', '#66c2a5', '#3288bd', '#5e4fa2'],

    // Sequential palettes  
    blues: ['#f7fbff', '#deebf7', '#c6dbef', '#9ecae1', '#6baed6', '#4292c6', '#2171b5', '#08519c', '#08306b'],
    reds: ['#fff5f0', '#fee0d2', '#fcbba1', '#fc9272', '#fb6a4a', '#ef3b2c', '#cb181d', '#a50f15', '#67000d'],

    // WeaverBI theme palette
    weaverGold: ['#1a1a2e', '#2d2d44', '#3d3d5c', '#7a5c00', '#a67c00', '#d4a017', '#f59e0b', '#fbbf24', '#fcd34d', '#fef3c7']
};

// Marker shapes SVG paths for consistency
export const MARKER_SHAPES = {
    circle: 'M0,0 m-1,0 a1,1 0 1,0 2,0 a1,1 0 1,0 -2,0',
    square: 'M-1,-1 L1,-1 L1,1 L-1,1 Z',
    triangle: 'M0,-1 L1,1 L-1,1 Z',
    diamond: 'M0,-1 L1,0 L0,1 L-1,0 Z',
    star: 'M0,-1 L0.22,-0.31 L1,-0.31 L0.36,0.12 L0.59,0.81 L0,0.38 L-0.59,0.81 L-0.36,0.12 L-1,-0.31 L-0.22,-0.31 Z',
    cross: 'M-0.3,-1 L0.3,-1 L0.3,-0.3 L1,-0.3 L1,0.3 L0.3,0.3 L0.3,1 L-0.3,1 L-0.3,0.3 L-1,0.3 L-1,-0.3 L-0.3,-0.3 Z'
};

// Line style dash arrays for SVG
export const LINE_DASH_ARRAYS = {
    solid: '',
    dashed: '8 4',
    dotted: '2 2'
};
